import { IconSearch, IconUserPlus } from "@tabler/icons-react";
import { Button, Checkbox, TextInput } from "@mantine/core";
import { useMemo, useState } from "react";

import { Modal } from "antd";
import type { RfqPartnerOption } from "@/lib/rfq/api";
import { useRfqLabels } from "@/lib/rfq/labels";

function RfqAddVendorModal({
  open,
  onClose,
  partners,
  invitedPartnerIds,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  partners: RfqPartnerOption[];
  invitedPartnerIds: string[];
  onAdd: (partnerIds: string[]) => Promise<void> | void;
}) {
  const { t } = useRfqLabels();
  const [keyword, setKeyword] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const invited = useMemo(
    () => new Set(invitedPartnerIds),
    [invitedPartnerIds],
  );

  const filtered = useMemo(() => {
    const value = keyword.trim().toLowerCase();
    if (!value) return partners;
    return partners.filter(
      (partner) =>
        partner.name.toLowerCase().indexOf(value) >= 0 ||
        partner.taxId.indexOf(value) >= 0,
    );
  }, [partners, keyword]);

  const toggle = (id: string) =>
    setPicked((prev) =>
      prev.indexOf(id) >= 0
        ? prev.filter((value) => value !== id)
        : [...prev, id],
    );

  const submit = async () => {
    setSaving(true);
    try {
      await onAdd(picked);
      setPicked([]);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={640}
      footer={null}
      destroyOnClose
      styles={{ body: { padding: 0 } }}
      title={
        <span className="flex items-center gap-2 text-base font-medium text-gray-700">
          <IconUserPlus size={18} className="text-blue-600" />
          {t("rfq.vendor.addVendor") || "加入廠商"}
        </span>
      }
    >
      <div className="border-t border-gray-200 p-3">
        <TextInput
          value={keyword}
          onChange={(event) => setKeyword(event.currentTarget.value)}
          placeholder={t("rfq.vendor.search") || "搜尋廠商名稱 / 統編"}
          leftSection={<IconSearch size={15} />}
          aria-label={t("rfq.vendor.search") || "搜尋廠商名稱 / 統編"}
        />
      </div>
      <div className="max-h-[50vh] overflow-y-auto border-t border-gray-200">
        {filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500">
            {t("rfq.vendor.noPartner") || "查無合作方。"}
          </p>
        ) : (
          filtered.map((partner) => {
            const already = invited.has(partner.id);
            return (
              <div
                key={partner.id}
                className="flex items-center gap-3 border-b border-gray-100 px-4 py-2.5 last:border-0"
              >
                <Checkbox
                  size="xs"
                  disabled={already}
                  checked={picked.indexOf(partner.id) >= 0}
                  onChange={() => toggle(partner.id)}
                  aria-label={partner.name}
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-700">
                    {partner.name}
                  </div>
                  <div className="truncate text-xs text-gray-500">
                    {partner.taxId || "—"}
                    {partner.contact ? ` ・ ${partner.contact}` : ""}
                    {partner.phone ? ` ・ ${partner.phone}` : ""}
                  </div>
                </div>
                {already ? (
                  <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                    {t("rfq.vendor.alreadyInvited") || "已加入"}
                  </span>
                ) : null}
              </div>
            );
          })
        )}
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-200 px-4 py-3">
        <Button variant="default" radius="md" onClick={onClose}>
          {t("rfq.vendor.cancel") || "取消"}
        </Button>
        <Button
          radius="md"
          color="blue"
          loading={saving}
          disabled={picked.length === 0}
          onClick={submit}
        >
          {t("rfq.vendor.addConfirm", {
            defaultValue: "加入 {{n}} 家",
            n: picked.length,
          })}
        </Button>
      </div>
    </Modal>
  );
}

export default RfqAddVendorModal;
